import { v4 as uuid } from 'uuid';

import castGraphQLObject from '../castGraphQLObject';
import constructContent, {
  ConstructContentContentType,
  CONSTRUCT_CONTENT_CONTENT_TYPES,
} from './constructContent';
import { BlockInstanceType, Channel } from 'application-types';
import { ContentType, ContentTemplate } from 'graphql-query-contracts';

interface CopyTemplateParams {
  template: ContentTemplate;
  channel: Required<Pick<Channel, '_id' | 'address' | 'profile'>>;
  shouldClearIconColors?: boolean;
}

export function copyTemplateForContent({
  template,
  channel,
  shouldClearIconColors = false,
}: CopyTemplateParams) {
  // A deep clone is necessary to bypass template caching issue
  template = castGraphQLObject(JSON.parse(JSON.stringify(template)));

  const baseContent = constructContent({
    contentType: mapContentTypeToEnum(template.type),
    channel,
  });

  // copy template to current content.
  const {
    actions,
    backgroundColor,
    backgroundImage,
    block,
    card,
    category,
    color,
    data,
    description,
    description_l10n,
    endDate,
    features,
    icon,
    iconSet,
    iconWeight,
    isEvent,
    isInteractive,
    lastReset,
    logo,
    name,
    name_l10n,
    nextReset,
    properties,
    renderer,
    reportingGroup,
    resetPeriod,
    startDate,
    state,
    subtitle,
    subtitle_l10n,
    tags,
    contentTags,
    type,
    version,
  } = template;

  // if there are any features, or actions, give them new ids.
  if (features) {
    features.forEach((feature: any) => {
      feature._id = uuid();
    });
  }

  if (actions) {
    actions.forEach((action: ContentTemplate['actions']) => {
      action._id = uuid();
    });
  }

  if (block) {
    block._id = uuid();

    if (shouldClearIconColors) {
      clearIconColors(block, template);
    }
  }

  return {
    ...baseContent,
    backgroundColor,
    backgroundImage,
    block,
    card,
    category,
    color,
    data,
    description,
    description_l10n,
    endDate,
    features,
    icon,
    iconSet,
    iconWeight,
    isEvent,
    isInteractive,
    lastReset,
    logo,
    name,
    name_l10n,
    nextReset,
    properties,
    renderer,
    reportingGroup,
    resetPeriod,
    startDate,
    state,
    subtitle,
    subtitle_l10n,
    tags,
    contentTags,
    type,
    version,
  };
}

function shouldClearIconColor(
  template: ContentTemplate,
  iconColor?: string | null
): boolean {
  // Only template.theme is available from the GraphQL query
  // template.channel.profile is not queried in the fragment
  const templateTheme = template.theme;

  // The fallback icon color if a custom one is not supplied
  const templateThemeSecondary = templateTheme?.palette?.secondary;

  // No theme (is likely a VTS Template) means we want to use destination channel theme
  const shouldClearAllIconColors = !templateTheme;

  const iconColorMatchesTemplateThemeSecondary =
    iconColor === templateThemeSecondary;

  return shouldClearAllIconColors || iconColorMatchesTemplateThemeSecondary;
}

/**
 * Maps GraphQL ContentType enum to ConstructContentContentType.
 * Returns only the types supported by constructContent.
 */
function mapContentTypeToEnum(
  graphqlType: ContentType
): ConstructContentContentType {
  const supportedType = CONSTRUCT_CONTENT_CONTENT_TYPES.find(
    value => String(value) === String(graphqlType)
  );

  if (supportedType) {
    return supportedType;
  }

  throw new Error(
    `copyTemplateForContent - Invalid ContentType: ${graphqlType}`
  );
}

/**
 * Clears icon colors from template blocks when they match the template's theme secondary color.
 * This ensures icons inherit the destination channel's theme color instead of the source template's color.
 *
 * @param block - The block instance to traverse and clear icon colors from
 * @param template - The template object containing theme information
 */
function clearIconColors(block: BlockInstanceType, template: ContentTemplate) {
  const traverseAndClear = (blockInstance: BlockInstanceType) => {
    if (blockInstance?.properties) {
      Object.keys(blockInstance.properties).forEach(key => {
        const prop = blockInstance.properties[key];

        if (key === 'icon' && prop && typeof prop === 'object') {
          if (shouldClearIconColor(template, prop.color)) {
            prop.color = null;
          }
        }

        if (prop?._id) {
          traverseAndClear(prop);
        }

        if (Array.isArray(prop)) {
          prop.forEach(item => {
            if (item?._id) {
              traverseAndClear(item);
            }
          });
        }
      });
    }

    if (blockInstance?.children && Array.isArray(blockInstance.children)) {
      blockInstance.children.forEach((child: BlockInstanceType) =>
        traverseAndClear(child)
      );
    }
  };

  traverseAndClear(block);
}
